import { ChatConversation } from '../../../modules/labraChat/useChatConversations'
import { calendarDaysAgo } from '../../../utils/dates'

export const TODAY = 'Today'
export const YESTERDAY = 'Yesterday'
export const BEYOND_7_DAYS = 'Beyond 7 days'
export const BEYOND_30_DAYS = 'Beyond 30 days'
export const BEYOND_90_DAYS = 'Beyond 90 days'
export const BEYOND_1_YEAR = 'Beyond 1 year'

/** Buckets the plan covers, in display order. */
const OPEN_GROUPS = [TODAY, YESTERDAY, BEYOND_7_DAYS, BEYOND_30_DAYS]

/** Buckets that need a bigger plan, so they never list conversations. */
export const LOCKED_GROUPS = [
  { label: BEYOND_90_DAYS, period: '90 days' },
  { label: BEYOND_1_YEAR, period: '1 year' },
]

export interface ConversationGroup {
  label: string
  items: ChatConversation[]
}

/** Conversations older than this need a bigger plan, so they are never listed. */
const OPEN_RANGE_DAYS = 30

const daysAgoOf = (iso: string, now: number): number | null => {
  const then = new Date(iso).getTime()
  if (!iso || Number.isNaN(then)) return null
  return calendarDaysAgo(then, now)
}

const bucketFor = (iso: string, now: number): string | null => {
  const daysAgo = daysAgoOf(iso, now)
  if (daysAgo === null) return null
  if (daysAgo <= 0) return TODAY
  if (daysAgo === 1) return YESTERDAY
  if (daysAgo <= 7) return BEYOND_7_DAYS
  if (daysAgo <= OPEN_RANGE_DAYS) return BEYOND_30_DAYS
  return null
}

/**
 * True once the loaded pages reach past the open range. Sessions arrive
 * newest-first, so no later page can hold anything the list would show.
 */
export const hasReachedLockedRange = (
  conversations: ChatConversation[],
  now: number = Date.now()
): boolean =>
  conversations.some(({ updatedAt }) => {
    const daysAgo = daysAgoOf(updatedAt, now)
    return daysAgo !== null && daysAgo > OPEN_RANGE_DAYS
  })

/**
 * Group conversations by `updatedAt` into the buckets the plan covers, keeping
 * their incoming order and dropping empty buckets. Anything older than 30 days
 * belongs to a locked bucket and is left out.
 */
export const bucketConversationsByRecency = (
  conversations: ChatConversation[],
  now: number = Date.now()
): ConversationGroup[] => {
  const byLabel = new Map<string, ChatConversation[]>()

  for (const conversation of conversations) {
    const label = bucketFor(conversation.updatedAt, now)
    if (!label) continue
    const bucket = byLabel.get(label)
    if (bucket) bucket.push(conversation)
    else byLabel.set(label, [conversation])
  }

  return OPEN_GROUPS.filter(label => byLabel.has(label)).map(label => ({
    label,
    items: byLabel.get(label) as ChatConversation[],
  }))
}
